import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";

import context from "../../store/context";

// layout
import AuthenticationLayout from "./AuthenticationLayout";

import AuthenticationButton from "../../components/Buttons/AuthenticationButton";
import AlertModal from "../../components/UI/AlertModal";

//icons
import ArrowIcon from "../../components/icons/Arrow";

//assets
import KachaLogo from "../../assets/kacha.png";
import LoginPattern from "../../assets/login-pattern.svg";

const MINIAPP_ENDPOINT = process.env.REACT_APP_MINIAPP_ENDPOINT;

const LoginPage = () => {
  const [isSending, setIsSending] = useState(false);
  const [alert, setAlert] = useState(null);

  const { setToken } = useContext(context);

  const navigate = useNavigate();

  const showError = (title, message) => {
    setIsSending(false);
    setAlert({ title, message });
  };

  // log the customer in — kacha-login exchanges the superapp's session token;
  // the backend resolves the real phone/name itself via Kacha's user_kyc endpoint
  const kachaLogin = async (sessionToken) => {
    try {
      const res = await fetch(`${MINIAPP_ENDPOINT}/api/miniapp/kacha-login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ session_token: sessionToken }),
      });
      const json = await res.json().catch(() => null);
      console.log("status", res.status, json);

      if (json && json.status && json.token) {
        setIsSending(false);
        sessionStorage.setItem("token", json.token);
        setToken(json.token);
        navigate("/tenders");
      } else {
        // backend reachable but rejected the login — keep the raw
        // backend reason out of the UI, show a friendly message instead
        showError(
          "Couldn't Sign You In",
          "Something went wrong. Please try again."
        );
      }

      return json;
    } catch (error) {
      console.log("error", error);
      showError(
        "Connection Problem",
        "We couldn't reach iChereta. Check your connection and try again."
      );
      return null;
    }
  };

  const logUserIn = () => {
    // Kacha SuperApp injects window.session with its session token; if it's
    // missing, the page was opened directly outside the superapp WebView
    if (!window.session) {
      setAlert({
        title: "Open in Kacha SuperApp",
        message:
          "This app needs to be opened from within the Kacha SuperApp. Please launch iChereta from the Kacha SuperApp to continue.",
      });
      return;
    }

    setIsSending(true);
    kachaLogin(window.session);
  };

  return (
    <AuthenticationLayout
      button="Sign Up"
      route="/signup"
      message="Don't have an account?"
      footerClassName="text-[#d8ccf7]"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-0 h-[85%] bg-gradient-to-t from-[#664bc7]/80 via-[#664bc7]/30 via-60% to-transparent"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-0 h-[85%]"
        style={{
          backgroundImage: `url(${LoginPattern})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "bottom",
          opacity: 0.6,
        }}
      />
      <div className="relative z-10 w-full flex flex-col items-center space-y-5">
        <h1 className="font-bold text-lg text-[#5a4aa6]">ሁሉንም ጨረታ በአንድ ቦታ!</h1>
        <small className="text-center text-xs font-bold text-[#5a4aa6]">በመረጧቸው ዘርፎች ያሉ የጨረታ ዝርዝር መረጃዎችን በቀላሉ ይግኙ!</small>
        <div className=" animate-bounce">
          <ArrowIcon styles="w-8 h-8 rotate-90 text-[#5a4aa6]" />
        </div>
        <div className="w-[90%] max-w-[600px] bg-transparent flex flex-col flex-grow  sm:p-10 md:rounded-md space-y-3 md:space-y-5 ">
          <AuthenticationButton
            process={isSending}
            className="w-full h-12 md:h-[52px] bg-white text-[#3a2f6b] text-base md:text-lg rounded-xl overflow-hidden relative border border-gray-200 shadow-[0_2px_4px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.9)] hover:shadow-[0_4px_10px_rgba(0,0,0,0.12),inset_0_1px_0_rgba(255,255,255,0.9)] active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.12)] active:translate-y-px transition-all duration-200"
            name={
              <span className="flex items-center justify-center gap-3 font-bold">
                <img
                  src={KachaLogo}
                  alt="Kacha SuperApp"
                  decoding="sync"
                  loading="eager"
                  className="h-7 w-7 object-contain"
                />
                <span className="h-6 w-px bg-gray-300" />
                Login with Kacha SuperApp
              </span>
            }
            onClick={logUserIn}
          />
        </div>
      </div>

      {alert && (
        <AlertModal
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </AuthenticationLayout>
  );
};

export default LoginPage;
